<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import { isHotelIngredient } from '@apps/main/modules/recipe/ingredients';
import type { HotelIngredient } from '@apps/main/modules/recipe/ingredients';
import type { OwnStayIngredient } from '@apps/main/modules/recipe/ingredients/ownStayIngredient';

import type { NeighborhoodPolygonsOnlyHotel } from '@libs/trip-planner-interfaces';
import { FULL_STAR_LIST } from '@libs/trip-planner-interfaces';
import type { AppInputTheme } from '@libs/ui';
import { AppCheckbox, AppInput, AppStarsSelector, AppSvgIcon } from '@libs/ui';

import { useFiltersStore } from '../../core/stores/filters/filtersStore';

import HotelFiltersSection from './components/HotelFiltersSection.vue';
import type { HotelFilterUpdate } from './model/hotelFilters';
import { HotelFilter } from './model/hotelFilters';
import { useHotelFiltersStore } from './store/hotelFiltersStore';

interface HotelFiltersProps {
  theme: AppInputTheme;
  ingredient: HotelIngredient | OwnStayIngredient | null;
  hideFiltersHeaders?: boolean;
  maxLength?: number;
  disableHotelNameFilter?: boolean;
}

const props = defineProps<HotelFiltersProps>();

interface HotelFiltersEmits {
  (eventName: 'update-filter', updateEvent: HotelFilterUpdate): void;
}

const emit = defineEmits<HotelFiltersEmits>();
const hotelFiltersStore = useHotelFiltersStore();
const filtersStore = useFiltersStore();

const areExtendedNeighborhoodFilters = ref<boolean>(false);
const MIN_NEIGHBORHOOD_FILTERS = 10;
const shouldAllowExtendableNeighborhoodFilter = computed<boolean>(
  () =>
    !!props.ingredient &&
    isHotelIngredient(props.ingredient) &&
    props.ingredient.neighborhoodPolygons.length > MIN_NEIGHBORHOOD_FILTERS
);

watch(
  () => props.ingredient,
  () => {
    if (props.ingredient) {
      hotelFiltersStore.setFiltersValuesFromIngredient(props.ingredient);
    }
  },
  {
    immediate: true,
    deep: true,
  }
);

const areNeighborhoodPolygons = computed<boolean>(
  () =>
    !!props.ingredient &&
    isHotelIngredient(props.ingredient) &&
    !!props.ingredient.neighborhoodPolygons.length
);

const topNeighborhoodsToDisplay = computed<
  NeighborhoodPolygonsOnlyHotel[] | undefined
>(() =>
  !!props.ingredient && isHotelIngredient(props.ingredient)
    ? [...props.ingredient.neighborhoodPolygons].sort(
        (a, b) => b.score - a.score
      )
    : undefined
);

function toggleExtendedNeighborhoodFilters(): void {
  areExtendedNeighborhoodFilters.value = !areExtendedNeighborhoodFilters.value;
}

function handleFilterUpdate(updateContext: HotelFilterUpdate): void {
  hotelFiltersStore.updateFormValue(updateContext);
  emit('update-filter', updateContext);
}
</script>

<template>
  <div class="hotel-filters">
    <HotelFiltersSection
      v-if="!props.disableHotelNameFilter"
      :border-separator="false"
    >
      <AppInput
        :input-text="hotelFiltersStore.form.hotelName"
        :placeholder="$t('filter.filter_by_name')"
        :theme="props.theme"
        :max-length="props.maxLength"
        :debounce-delay="300"
        @text-changed="
          handleFilterUpdate({
            type: HotelFilter.HotelName,
            value: $event,
          })
        "
      >
        <template #left-icon>
          <AppSvgIcon name="lookingGlass" size="18" />
        </template>
      </AppInput>
    </HotelFiltersSection>

    <HotelFiltersSection
      :title="
        props.hideFiltersHeaders
          ? undefined
          : $t('filter.boardBasis.description')
      "
      :border-separator="true"
    >
      <AppCheckbox
        :theme="props.theme === 'dark' ? 'grey-dark' : 'white'"
        box-position="right"
        size="small"
        :checked="hotelFiltersStore.form.meals.noMeals"
        @change="
          handleFilterUpdate({
            type: HotelFilter.MealsNoMeals,
            value: $event,
          })
        "
      >
        {{ $t('filter.boardBasis.no_meals') }}
      </AppCheckbox>
      <AppCheckbox
        :theme="props.theme === 'dark' ? 'grey-dark' : 'white'"
        box-position="right"
        size="small"
        :checked="hotelFiltersStore.form.meals.breakfast"
        @change="
          handleFilterUpdate({
            type: HotelFilter.MealsBreakfast,
            value: $event,
          })
        "
      >
        {{ $t('filter.boardBasis.breakfast') }}
      </AppCheckbox>
      <AppCheckbox
        :theme="props.theme === 'dark' ? 'grey-dark' : 'white'"
        box-position="right"
        size="small"
        :checked="hotelFiltersStore.form.meals.halfBoard"
        @change="
          handleFilterUpdate({
            type: HotelFilter.MealsHalfBoard,
            value: $event,
          })
        "
      >
        {{ $t('filter.boardBasis.half_board') }}
      </AppCheckbox>
      <AppCheckbox
        :theme="props.theme === 'dark' ? 'grey-dark' : 'white'"
        box-position="right"
        size="small"
        :checked="hotelFiltersStore.form.meals.fullBoard"
        @change="
          handleFilterUpdate({
            type: HotelFilter.MealsFullBoard,
            value: $event,
          })
        "
      >
        {{ $t('filter.boardBasis.full_board') }}
      </AppCheckbox>
      <AppCheckbox
        :theme="props.theme === 'dark' ? 'grey-dark' : 'white'"
        box-position="right"
        size="small"
        :checked="hotelFiltersStore.form.meals.allInclusive"
        @change="
          handleFilterUpdate({
            type: HotelFilter.MealsAllInclusive,
            value: $event,
          })
        "
      >
        {{ $t('filter.boardBasis.all_inclusive') }}
      </AppCheckbox>
    </HotelFiltersSection>

    <HotelFiltersSection
      :title="
        props.hideFiltersHeaders
          ? undefined
          : $t('filter.amenities.description')
      "
      :border-separator="true"
    >
      <AppCheckbox
        :theme="props.theme === 'dark' ? 'grey-dark' : 'white'"
        box-position="right"
        size="small"
        :checked="hotelFiltersStore.form.amenities.wifi"
        @change="
          handleFilterUpdate({
            type: HotelFilter.AmenitiesWifi,
            value: $event,
          })
        "
      >
        {{ $t('filter.amenities.wifi') }}
      </AppCheckbox>
      <AppCheckbox
        :theme="props.theme === 'dark' ? 'grey-dark' : 'white'"
        box-position="right"
        size="small"
        :checked="hotelFiltersStore.form.amenities.gym"
        @change="
          handleFilterUpdate({
            type: HotelFilter.AmenitiesGym,
            value: $event,
          })
        "
      >
        {{ $t('filter.amenities.gym') }}
      </AppCheckbox>
      <AppCheckbox
        :theme="props.theme === 'dark' ? 'grey-dark' : 'white'"
        box-position="right"
        size="small"
        :checked="hotelFiltersStore.form.amenities.airConditioned"
        @change="
          handleFilterUpdate({
            type: HotelFilter.AmenitiesAirConditioned,
            value: $event,
          })
        "
      >
        {{ $t('filter.amenities.air_conditioned') }}
      </AppCheckbox>
      <AppCheckbox
        :theme="props.theme === 'dark' ? 'grey-dark' : 'white'"
        box-position="right"
        size="small"
        :checked="hotelFiltersStore.form.amenities.spa"
        @change="
          handleFilterUpdate({
            type: HotelFilter.AmenitiesSpa,
            value: $event,
          })
        "
      >
        {{ $t('filter.amenities.spa') }}
      </AppCheckbox>
      <AppCheckbox
        :theme="props.theme === 'dark' ? 'grey-dark' : 'white'"
        box-position="right"
        size="small"
        :checked="hotelFiltersStore.form.amenities.pool"
        @change="
          handleFilterUpdate({
            type: HotelFilter.AmenitiesPool,
            value: $event,
          })
        "
      >
        {{ $t('filter.amenities.pool') }}
      </AppCheckbox>
      <AppCheckbox
        :theme="props.theme === 'dark' ? 'grey-dark' : 'white'"
        box-position="right"
        size="small"
        :checked="hotelFiltersStore.form.amenities.parking"
        @change="
          handleFilterUpdate({
            type: HotelFilter.AmenitiesParking,
            value: $event,
          })
        "
      >
        {{ $t('filter.amenities.parking') }}
      </AppCheckbox>
      <AppCheckbox
        :theme="props.theme === 'dark' ? 'grey-dark' : 'white'"
        box-position="right"
        size="small"
        :checked="hotelFiltersStore.form.amenities.vehicleChargingStation"
        @change="
          handleFilterUpdate({
            type: HotelFilter.AmenitiesVehicleChargingStation,
            value: $event,
          })
        "
      >
        {{ $t('filter.amenities.electric_vehicle_charging_station') }}
      </AppCheckbox>
    </HotelFiltersSection>

    <HotelFiltersSection
      :title="
        props.hideFiltersHeaders
          ? undefined
          : $t('filter.type_of_accommodation')
      "
      :border-separator="true"
    >
      <AppCheckbox
        :theme="props.theme === 'dark' ? 'grey-dark' : 'white'"
        box-position="right"
        size="small"
        :checked="hotelFiltersStore.form.typeOfAccommodation.hotel"
        @change="
          handleFilterUpdate({
            type: HotelFilter.AccommodationTypeHotel,
            value: $event,
          })
        "
      >
        {{ $t('filter.hotelTypes.hotels') }}
      </AppCheckbox>
      <AppCheckbox
        :theme="props.theme === 'dark' ? 'grey-dark' : 'white'"
        box-position="right"
        size="small"
        :checked="hotelFiltersStore.form.typeOfAccommodation.apartment"
        @change="
          handleFilterUpdate({
            type: HotelFilter.AccommodationTypeApartment,
            value: $event,
          })
        "
      >
        {{ $t('filter.hotelTypes.apartments') }}
      </AppCheckbox>
      <AppCheckbox
        :theme="props.theme === 'dark' ? 'grey-dark' : 'white'"
        box-position="right"
        size="small"
        :checked="hotelFiltersStore.form.typeOfAccommodation.houseOrCottage"
        @change="
          handleFilterUpdate({
            type: HotelFilter.AccommodationTypeHouseOrCottage,
            value: $event,
          })
        "
      >
        {{ $t('filter.hotelTypes.housesAndCottages') }}
      </AppCheckbox>
    </HotelFiltersSection>

    <HotelFiltersSection
      :title="props.hideFiltersHeaders ? undefined : $t('filter.rating')"
      :border-separator="true"
    >
      <AppStarsSelector
        :options="FULL_STAR_LIST"
        :selected="hotelFiltersStore.form.rating.stars"
        @select="
          handleFilterUpdate({
            type: HotelFilter.RatingStars,
            value: $event,
          })
        "
      />
    </HotelFiltersSection>

    <HotelFiltersSection
      :title="
        props.hideFiltersHeaders
          ? undefined
          : $t('filter.travel_experience.description')
      "
      :border-separator="true"
    >
      <AppCheckbox
        :theme="props.theme === 'dark' ? 'grey-dark' : 'white'"
        box-position="right"
        size="small"
        :checked="hotelFiltersStore.form.experience.family"
        @change="
          handleFilterUpdate({
            type: HotelFilter.ExperienceFamily,
            value: $event,
          })
        "
      >
        {{ $t('filter.travel_experience.family') }}
      </AppCheckbox>
      <AppCheckbox
        :theme="props.theme === 'dark' ? 'grey-dark' : 'white'"
        box-position="right"
        size="small"
        :checked="hotelFiltersStore.form.experience.business"
        @change="
          handleFilterUpdate({
            type: HotelFilter.ExperienceBusiness,
            value: $event,
          })
        "
      >
        {{ $t('filter.travel_experience.business') }}
      </AppCheckbox>
      <AppCheckbox
        :theme="props.theme === 'dark' ? 'grey-dark' : 'white'"
        box-position="right"
        size="small"
        :checked="hotelFiltersStore.form.experience.petsAllowed"
        @change="
          handleFilterUpdate({
            type: HotelFilter.ExperiencePetsAllowed,
            value: $event,
          })
        "
      >
        {{ $t('filter.travel_experience.pets') }}
      </AppCheckbox>
      <AppCheckbox
        :theme="props.theme === 'dark' ? 'grey-dark' : 'white'"
        box-position="right"
        size="small"
        :checked="hotelFiltersStore.form.experience.greenSustainableProperty"
        @change="
          handleFilterUpdate({
            type: HotelFilter.ExperienceGreenSustainableProperty,
            value: $event,
          })
        "
      >
        {{ $t('filter.travel_experience.sustainable_property') }}
      </AppCheckbox>
    </HotelFiltersSection>

    <HotelFiltersSection
      :title="
        props.hideFiltersHeaders ? undefined : $t('filter.rules.description')
      "
      :border-separator="areNeighborhoodPolygons"
    >
      <AppCheckbox
        :theme="props.theme === 'dark' ? 'grey-dark' : 'white'"
        box-position="right"
        size="small"
        :checked="hotelFiltersStore.form.rules.freeCancellation"
        @change="
          handleFilterUpdate({
            type: HotelFilter.RuleFreeCancellation,
            value: $event,
          })
        "
      >
        {{ $t('filter.rules.free_cancellation') }}
      </AppCheckbox>
    </HotelFiltersSection>

    <HotelFiltersSection
      v-if="
        areNeighborhoodPolygons && ingredient && isHotelIngredient(ingredient)
      "
      :title="props.hideFiltersHeaders ? undefined : $t('filter.neighbourhood')"
      :border-separator="false"
      :extendable="shouldAllowExtendableNeighborhoodFilter"
      :limits="{
        collapsed: MIN_NEIGHBORHOOD_FILTERS,
        extended: filtersStore.DEFAULT_MAX_EXTENDED_FILTERS,
      }"
      :items="topNeighborhoodsToDisplay ?? []"
      @toggle-expanded="toggleExtendedNeighborhoodFilters"
    >
      <template #default="{ items }">
        <AppCheckbox
          v-for="neighborhoodPolygon in items as NeighborhoodPolygonsOnlyHotel[]"
          :key="neighborhoodPolygon.id"
          :theme="props.theme === 'dark' ? 'grey-dark' : 'white'"
          box-position="right"
          size="small"
          :checked="
            hotelFiltersStore.form.neighborhoodPolygons.includes(
              neighborhoodPolygon.id
            )
          "
          @change="
            handleFilterUpdate({
              type: HotelFilter.NeighborhoodPolygons,
              value: neighborhoodPolygon.id,
            })
          "
        >
          {{ neighborhoodPolygon.name }}
        </AppCheckbox>
      </template>
    </HotelFiltersSection>
  </div>
</template>
